import type { UseFetchOptions } from 'nuxt/app'
import type {
  EventListPageTypeString,
  StrapiEventListPage,
} from '~/composables/types/strapiEventListPage'
import type { StrapiApiDefaultResponse } from '~/composables/types/strapi-api'
import { EventListPageType } from '~/composables/types/strapiEventListPage'
import type { KftApiDefaultResponse } from '~/composables/types/kft-api'
import type { KftDynamicContentMetrics } from '~/utils/kftApiUtils'
import { strapiEventListPageGetters } from '~/utils/strapiEventListPageGetters'

export const useFetchEventListPageDynamicContent = (
  pageType: EventListPageTypeString,
  {
    citySlug,
    categorySlug,
    generalSlug,
  }: { citySlug?: string; categorySlug?: string; generalSlug?: string }
) => {
  const pageSlug = {
    [EventListPageType.CITY]: citySlug,
    [EventListPageType.CATEGORY]: categorySlug,
    [EventListPageType.CITY_CATEGORY]: `${citySlug}-${categorySlug}`,
    [EventListPageType.GENERAL]: generalSlug,
    [EventListPageType.TEAMEVENT]: generalSlug,
  }

  const mapResource = {
    [EventListPageType.CITY]: 'Locality',
    [EventListPageType.CATEGORY]: 'Category',
    [EventListPageType.CITY_CATEGORY]: 'LocalityCategory',
    [EventListPageType.GENERAL]: 'Tag',
    [EventListPageType.TEAMEVENT]: 'Tag',
  }

  return useKftApiFetch<KftApiDefaultResponse<KftDynamicContentMetrics>>(
    `/v1/store/metrics/${mapResource[pageType]}/${pageSlug[pageType]}`
  )
}

export function useStrapiEventListPage({
  slug,
  citySlug,
  categorySlug,
  options = {},
  locale,
}: {
  slug: string
  citySlug?: string
  categorySlug?: string
  options?: UseFetchOptions<
    StrapiApiDefaultResponse<StrapiEventListPage[]>,
    StrapiEventListPage[]
  >
  locale?: string
}) {
  const { locales } = useI18n()
  // If locale is provided, use the original implementation
  if (locale) {
    const eventListPageURL = getEventListPageStrapiURL(
      locale,
      slug,
      citySlug,
      categorySlug
    )

    return useStrapi<StrapiEventListPage>({
      urlSuffix: eventListPageURL,
      options,
    })
  }

  // When no locale is provided, fetch for each locale and combine results
  return useAsyncData(
    `event-list-pages-${slug}-${citySlug}-${categorySlug}`,
    async () => {
      const fetchPromises = locales.value.map((locale) => {
        const eventListPageURL = getEventListPageStrapiURL(
          locale.code,
          slug,
          citySlug,
          categorySlug
        )

        return useStrapi<StrapiEventListPage>({
          urlSuffix: eventListPageURL,
          options,
        })
      })

      const results = await Promise.all(fetchPromises)
      const allPages = results.flatMap((result) => result.data.value || [])
      const uniquePages = new Map<string, StrapiEventListPage>()

      // First pass: add all pages to the map
      allPages.forEach((page) => {
        uniquePages.set(page.id.toString(), page)
      })

      // Second pass: merge localizations
      allPages.forEach((page) => {
        const localizations = strapiEventListPageGetters.getLocalizations(page)
        if (localizations) {
          localizations.forEach((localization) => {
            if (!uniquePages.has(localization.id.toString())) {
              uniquePages.set(localization.id.toString(), localization)
            }
          })
        }
      })

      return Array.from(uniquePages.values())
    },
    {
      server: true,
      lazy: false,
      ...options,
    }
  )
}
